import { RequiredAccessDTO } from '@west-australian-newspapers/publication-types'
import { SubscriptionType } from '../data/authentication'
import { RouteServices } from '../routing/page-definition'

interface SophiPageContentData {
    page: SophiPageData
    content: SophiContentData
}

export function getSophiContent(
    routeServices: RouteServices,
): SophiDataType | undefined {
    const environment = 'prod'
    const authState = routeServices.store.getState().authentication
    const resolution = routeServices.resolution

    if (resolution.type === 'server') {
        const resolved = resolution.resolution

        if (resolved.type === 'publication') {
            const publication = resolved.publication

            const sectionName = publication.topics.primary.parent
                ? publication.topics.primary.parent?.id
                : publication.topics.primary.id

            return {
                data: {
                    page: {
                        type: 'article',
                        datePublished: publication.publicationDate,
                        breadcrumb: mapSophiBreadcrumb(
                            publication.topics.primary.id,
                        ),
                        sectionName,
                    },
                    content: {
                        accessCategory: mapAccessCategory(
                            publication.requiredAccess,
                        ),
                        contentId: publication.id,
                        type: publication.kind,
                    },
                    environment: {
                        environment,
                        version: routeServices.appVersion,
                    },
                    visitor: {
                        isLoggedIn: authState.isLoggedIn,
                        type: mapVisitorType(authState.subscriptionType),
                    },
                },
            }
        }

        if (resolved.type === 'topic') {
            const topic = resolved.topic

            if (!topic) {
                return
            }

            const mappedTopicData = getTopicRouteData(topic.id, topic.title)

            return {
                data: {
                    ...mappedTopicData,
                    environment: {
                        environment,
                        version: routeServices.appVersion,
                    },
                    visitor: {
                        isLoggedIn: authState.isLoggedIn,
                        type: mapVisitorType(authState.subscriptionType),
                    },
                },
            }
        }
    }

    if (resolution.type === 'static') {
        const topic = resolution.key

        const title = topic.substring(topic.lastIndexOf('/') + 1, topic.length)

        const mappedTopicData = getTopicRouteData(topic, title)

        return {
            data: {
                ...mappedTopicData,
                environment: {
                    environment,
                    version: routeServices.appVersion,
                },
                visitor: {
                    isLoggedIn: authState.isLoggedIn,
                    type: mapVisitorType(authState.subscriptionType),
                },
            },
        }
    }

    return undefined
}

export function mapSophiBreadcrumb(topicId: string) {
    if (topicId.charAt(0) === '/') {
        topicId = topicId.slice(1)
    }
    return topicId.replace('/', ':')
}

function getTopicRouteData(
    breadcrumb: string,
    sectionName: string,
): SophiPageContentData {
    const isProbablyHomePage = breadcrumb === '' && sectionName === ''

    return {
        page: {
            type: 'section',
            breadcrumb: mapSophiBreadcrumb(breadcrumb),
            sectionName: isProbablyHomePage
                ? 'home'
                : sectionName.toLowerCase(),
        },
        content: {
            accessCategory: undefined,
            contentId: undefined,
            type: undefined,
        },
    }
}

function mapVisitorType(
    type: SubscriptionType,
): 'Anonymous' | 'Registered' | 'Subscriber' {
    switch (type) {
        case 'corporate':
        case 'corporate-site':
        case 'subscriber':
            return 'Subscriber'
        default:
            return 'Anonymous'
    }
}

function mapAccessCategory(
    type: RequiredAccessDTO,
): 'free access' | 'subscribers only' {
    switch (type.level) {
        case 'subscriber':
            return 'subscribers only'

        default:
            return 'free access'
    }
}
